import { useCreateArtwork } from "@/api/artwork/artwork";
import {
  appendNoDuplicateRecordingNumbersRefine,
  artworkSchema,
} from "@/api/artwork/artwork-validation";
import { toast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";

const schema = appendNoDuplicateRecordingNumbersRefine(
  artworkSchema.pick({
    id: true,
    oeuvresDescriptionNumeroEnregistrement: true,
  }),
);
type FormType = z.infer<typeof schema>;

type NewArtworkDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewArtworkDialog = ({ onOpenChange, open }: NewArtworkDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    defaultValues: {
      oeuvresDescriptionNumeroEnregistrement: "",
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });
  const { mutateAsync, isPending } = useCreateArtwork();
  const navigate = useNavigate();

  const onFormSubmit = async (data: FormType, shouldRedirect: boolean) => {
    try {
      const result = await mutateAsync(data);
      if (!shouldRedirect) {
        toast({
          title: t("newRecordSuccessfullyCreated"),
          duration: 3000,
          variant: "success",
        });
        form.reset();
        return;
      }
      navigate(`/edit/artwork/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("oeuvresDescriptionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      if (isAxiosError(err) && err.response?.status === 403) {
        toast({
          title: t("maxArtworksLimitReached"),
          duration: 3000,
          variant: "error",
        });
      }
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createAartwork")}
      onFormSubmit={onFormSubmit}
      form={form}
      isLoading={isPending}
    >
      <FormField
        control={form.control}
        name="oeuvresDescriptionNumeroEnregistrement"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("oeuvresDescriptionNumeroEnregistrement")}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewArtworkDialog;
